import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import { useLazyGetPerformanceTestReportQuery } from "store/api/performanceTestApi"
import { useAppDispatch } from "store/hooks"
import { showPerformanceTestReportModal } from "store/modalSlise"
import { setPerformanceTestReport, setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"
import { ReactComponent as IconPower } from "assets/svg/icon-power.svg"
import { ReactComponent as IconStatsReport } from "assets/svg/icon-stats-report.svg"
import { ReactComponent as IconPerformanceTest } from "assets/svg/menu-performance.svg"

import { convertDataToMonitoringTableRows } from "utils/convertData"
import decodeCustomUrlParam from "utils/decodeCustomUrlParam"
import { isMobileDevice } from "utils/deviceDetection"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import DeviceRebootModal from "modals/DeviceRebootModal/DeviceRebootModal"
import { PerformanceTestActionsModal } from "modals/PerformanceTestActionsModal"
import { StatsReportModal } from "modals/StatsReportModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { monitoringTableHeaderCells } from "./MonitoringTableHeaderCells"

const EditColumnsButtonContainer = styled.div`
  position: absolute;
  top: 25px;
  right: 25px;
`

interface MonitoringPageContentProps {
  devices: ApiTypes.Model.Device[]
  meta: ApiTypes.Model.Pagination | undefined
  isFetching: boolean
  onPerformanceTestSuccess: () => void
}

const MonitoringPageContent = ({ devices, isFetching, meta, onPerformanceTestSuccess }: MonitoringPageContentProps) => {
  const dispatch = useAppDispatch()
  const [showPerformanceTestActionsModal, setShowPerformanceTestActionsModal] = useState(false)
  const [showRebootModalDevice, setShowRebootModalDevice] = useState<ApiTypes.Model.Device | undefined>()
  const [selectedDeviceForStatsReport, setSelectedDeviceForStatsReport] = useState<ApiTypes.Model.Device | undefined>()
  const [monitoringReportAnnotations, setMonitoringReportAnnotations] = useState<MonitoringReportAnnotations | null>(
    null,
  )
  const { originalQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const [triggerGetDevice] = useLazyGetDevicesQuery()
  const [triggerGetPerformanceTestReport] = useLazyGetPerformanceTestReportQuery()

  useEffect(() => {
    if (originalQueryParams["custom_param"]) {
      const decodedParam = decodeCustomUrlParam(originalQueryParams["custom_param"])

      if (decodedParam) {
        setMonitoringReportAnnotations(decodedParam)
        setUrlQueryParams({ custom_param: null, sn: decodedParam.sn })
      }
    }
  }, [originalQueryParams])

  const [defaultPeriod, setDefaultPeriod] = useState<string | undefined>()
  const [defaultTab, setDefaultTab] = useState<"liveData" | "reporting" | undefined>()

  useEffect(() => {
    const openOffline = originalQueryParams["openOffline"]
    const sn = originalQueryParams["sn"]

    if (openOffline && sn && devices.length > 0) {
      const device = devices.find(item => item.sn === sn)

      if (device) {
        setDefaultPeriod("last30Days")
        setDefaultTab("reporting")
        setSelectedDeviceForStatsReport(device)
        setUrlQueryParams({ openOffline: null })
      }
    }
  }, [devices, originalQueryParams, setUrlQueryParams])

  useEffect(() => {
    if (monitoringReportAnnotations && devices.length > 0) {
      const device = devices.find(item => item.sn === monitoringReportAnnotations.sn)

      if (device) {
        setSelectedDeviceForStatsReport(device)
      }
    }
  }, [devices, monitoringReportAnnotations])

  const openPerformanceTestModal = async (testId: number, deviceSn: string) => {
    try {
      const deviceData = await triggerGetDevice({ sn: deviceSn })

      if (deviceData.error) {
        toast.error("Something went wrong, please try again")
        return
      }

      const device = deviceData.data?.data[0]
      const res = await triggerGetPerformanceTestReport(testId)

      if (res.data && device) {
        dispatch(
          setPerformanceTestReport({
            ...res?.data,
            customer: device.homeowner_name,
            zip_code: device.zip?.code,
            where_installed_name: device.residence_type?.name,
            timeZone: device.time_zone_text || "",
          }),
        )
        dispatch(showPerformanceTestReportModal(true))
      }
    } catch {
      toast.error("Failed to load performance test report")
    }
  }

  useEffect(() => {
    const testId = originalQueryParams?.testId
    const sn = originalQueryParams?.sn

    if (testId && sn) {
      const testIdNumber = Number(testId)
      openPerformanceTestModal(testIdNumber, sn)
    }
  }, [originalQueryParams?.testId, originalQueryParams?.sn])

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(monitoringTableHeaderCells, columnsData?.data?.config)
        : monitoringTableHeaderCells,
    [columnsData],
  )

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_monitoring_list" })
  }, [])

  const handleShowActionsModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    dispatch(
      setSelectedDevicesForPerformanceTest({
        isImmediate: true,
        devices: [
          ...[{ id, email: device?.email, sn: device?.sn, system_type: device?.system_type }],
        ] as ApiTypes.Model.PerformanceTestDevice[],
      }),
    )
    setShowPerformanceTestActionsModal(true)
  }

  const handleShowStatsReportModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    setSelectedDeviceForStatsReport(device)
  }

  const handleClearMonitoringReportAnnotations = () => {
    setMonitoringReportAnnotations(null)
  }

  const handleCancelStatsReportModal = () => {
    setSelectedDeviceForStatsReport(undefined)
    setDefaultPeriod(undefined)
    setDefaultTab(undefined)
    handleClearMonitoringReportAnnotations()
  }

  const handleShowRebootModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    setShowRebootModalDevice(device)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    const isOffline = rowData.rowSettings.item.work_status === "offline"
    const isRunning = rowData.rowSettings.item.perftest?.status === PerformanceTestStatus.RUNNING

    return {
      actionButtons: [
        {
          icon: <IconPower />,
          tooltipPosition: "top" as const,
          tooltip: (
            <>
              Reboot device
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
            </>
          ),
          disabled: isOffline,
          handler: handleShowRebootModal,
        },
        {
          icon: <IconPerformanceTest />,
          tooltipPosition: "top-start" as const,
          tooltip: (
            <>
              Start performance test
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
              {isRunning && !isOffline && (
                <>
                  <br />
                  (performance test is running)
                </>
              )}
            </>
          ),
          disabled: isOffline || isRunning,
          handler: handleShowActionsModal,
        },
        {
          icon: <IconStatsReport />,
          tooltipPosition: "top-start" as const,
          tooltip: "Monitoring report",
          handler: handleShowStatsReportModal,
        },
      ],
    }
  }

  return (
    <>
      <ContentWrapper title="Monitoring Page">
        <EditColumnsButtonContainer>
          <EditColumnsButton tableName="contractor_monitoring_list" tableHeaderCells={tableHeaderCells} />
        </EditColumnsButtonContainer>

        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          data={convertDataToMonitoringTableRows(devices)}
          getRowActions={getRowActions}
          isLoading={isFetching}
          isMobileDevice={isMobileDevice()}
        />
        <Pagination meta={meta} />
        {showRebootModalDevice && (
          <DeviceRebootModal device={showRebootModalDevice} onClose={() => setShowRebootModalDevice(undefined)} />
        )}
        {showPerformanceTestActionsModal && (
          <PerformanceTestActionsModal
            onCancel={() => setShowPerformanceTestActionsModal(false)}
            onSuccess={onPerformanceTestSuccess}
          />
        )}
      </ContentWrapper>
      {selectedDeviceForStatsReport && (
        <StatsReportModal
          onCancel={handleCancelStatsReportModal}
          device={selectedDeviceForStatsReport}
          monitoringReportAnnotations={monitoringReportAnnotations}
          onClearMonitoringReportAnnotations={handleClearMonitoringReportAnnotations}
          defaultPeriod={defaultPeriod}
          defaultTab={defaultTab}
        />
      )}
    </>
  )
}

export default MonitoringPageContent
